import Vue from "vue";
import mime from "mime-types";
import { BlobServiceClient } from "@azure/storage-blob";
import { CallLogType } from "@/store/modules/enums";

export async function uploadFile(
  entityId: string,
  file: File
): Promise<Number> {
  // GET FILE SLOT
  const response = await Vue.axios.post("files/slot");
  const { storageId, storageUri } = response.data;
  // UPLOAD FILE TO SLOT
  const blobServiceClient = new BlobServiceClient(storageUri);
  const containerName = "";
  const containerClient = blobServiceClient.getContainerClient(containerName);
  const blockBlobClient = containerClient.getBlockBlobClient(storageId);
  const blobHTTPHeaders = {
    blobContentType: mime.lookup(file.name) || "application/octet-stream",
    blobContentDisposition: "filename=" + file.name,
  };
  const metadata = {
    file_name: file.name,
    file_size: String(file.size),
  };
  await blockBlobClient.uploadBrowserData(file, {
    blobHTTPHeaders,
    metadata,
    blockSize: 4 * 1024 * 1024,
    concurrency: 20,
    onProgress: (ev) => console.log(ev),
  });
  // SAVE FILE INFO TO DB
  const params = new FormData();
  params.append("entityId", entityId);
  params.append("fileName", file.name);
  params.append("fileSize", file.size.toString());
  params.append("contentType", blobHTTPHeaders.blobContentType);
  params.append("storageId", storageId);
  const { data } = await Vue.axios.post("files/info/save", params);
  const { fileId } = data;
  // RETURN NEW FILE ID
  return fileId;
  //
}

export async function getFileViewUrl(fileId: number): Promise<string> {
  // SET FORM DATA
  const params = new FormData();
  params.append("fileId", fileId.toString());
  // GET FILE URL ASYNC
  const { data } = await Vue.axios.post("files/url", params);
  //
  return data.url;
}

export async function getFileDownloadUrl(fileId: number): Promise<string> {
  // SET FORM DATA
  const params = new FormData();
  params.append("fileId", fileId.toString());
  // GET FILE DOWNLOAD URL ASYNC
  const { data } = await Vue.axios.post("files/url/download", params);
  //
  return data.url;
}

export async function uploadRecording(
  entityId: string,
  file: File
): Promise<Number> {
  // GET RECORDING SLOT
  const response = await Vue.axios.post("callcenter/recording/slot");
  const { storageId, storageUri } = response.data;
  // UPLOAD RECORDING TO SLOT
  const blobServiceClient = new BlobServiceClient(storageUri);
  const containerName = "";
  const containerClient = blobServiceClient.getContainerClient(containerName);
  const blockBlobClient = containerClient.getBlockBlobClient(storageId);
  const blobHTTPHeaders = {
    blobContentType: mime.lookup(file.name) || "application/octet-stream",
    blobContentDisposition: "filename=" + file.name,
  };
  const metadata = {
    file_name: file.name,
    file_size: String(file.size),
  };
  await blockBlobClient.uploadBrowserData(file, {
    blobHTTPHeaders,
    metadata,
    blockSize: 4 * 1024 * 1024,
    concurrency: 20,
    onProgress: (ev) => console.log(ev),
  });
  // SAVE RECORDING FILE INFO TO DB
  const params = new FormData();
  params.append("entityId", entityId);
  params.append("fileName", file.name);
  params.append("fileSize", file.size.toString());
  params.append("contentType", blobHTTPHeaders.blobContentType);
  params.append("storageId", storageId);
  //
  const { data } = await Vue.axios.post("callcenter/recording/save", params);
  const { id } = data;
  // RETURN NEW RECORDING ID
  return id;
  //
}

export function recordingUrl(storageId: string): string {
  let { hostname } = window.location;
  let url = "https://wmcallcenter.blob.core.windows.net/";
  if (hostname.includes("localhost") || hostname.includes(".dev.")) {
    url += "dev/";
  } else if (hostname.includes(".qa.")) {
    url += "quality/";
  } else if (hostname.includes(".uat.")) {
    url += "uat/";
  } else {
    url += "prod/";
  }
  url += storageId;
  return url;
}

export async function uploadCallFile(
  callId: number,
  file: File,
  logTypeId: CallLogType
): Promise<number> {
  // GET FILE SLOT
  const response = await Vue.axios.post("callcenter/file/slot");
  const { storageId, storageUri } = response.data;
  // UPLOAD FILE TO SLOT
  const blobServiceClient = new BlobServiceClient(storageUri);
  const containerName = "";
  const containerClient = blobServiceClient.getContainerClient(containerName);
  const blockBlobClient = containerClient.getBlockBlobClient(storageId);
  const blobHTTPHeaders = {
    blobContentType: mime.lookup(file.name) || "application/octet-stream",
    blobContentDisposition: "filename=" + file.name,
  };
  const metadata = {
    file_name: file.name,
    file_size: String(file.size),
  };
  await blockBlobClient.uploadBrowserData(file, {
    blobHTTPHeaders,
    metadata,
    blockSize: 4 * 1024 * 1024,
    concurrency: 20,
    onProgress: (ev) => console.log(ev),
  });
  // SAVE FILE INFO TO DB
  const params = new FormData();
  params.append("callId", callId.toString());
  params.append("fileName", file.name);
  params.append("fileSize", file.size.toString());
  params.append("contentType", blobHTTPHeaders.blobContentType);
  params.append("storageId", storageId);
  params.append("logTypeId", logTypeId);
  //
  const { data } = await Vue.axios.post("callcenter/file/save", params);
  const { id } = data;
  // RETURN NEW FILE ID
  return id;
  //
}

export async function uploadBlob(
  blob: Blob,
  contentType: string | undefined = undefined
): Promise<string> {
  // GET FILE SLOT
  const response = await Vue.axios.post("callcenter/file/slot");
  const { storageId, storageUri } = response.data;
  // UPLOAD FILE TO SLOT
  const blobServiceClient = new BlobServiceClient(storageUri);
  const containerName = "";
  const containerClient = blobServiceClient.getContainerClient(containerName);
  const blockBlobClient = containerClient.getBlockBlobClient(storageId);
  const blobHTTPHeaders = {
    blobContentType: contentType || "application/octet-stream",
  };
  await blockBlobClient.uploadBrowserData(blob, {
    blobHTTPHeaders,
    blockSize: 4 * 1024 * 1024,
    concurrency: 20,
    onProgress: (event) => console.log(event),
  });
  // RETURN STORAGE ID
  return storageId;
  //
}

export async function archiveCallFile(id: number) {
  // SET PARAMS
  const params = new FormData();
  params.append("id", id.toString());
  //
  await Vue.axios.post("callcenter/file/archive", params);
  //
}

export function callFileUrl(storageId: string): string {
  const { hostname } = window.location;
  let url = "https://wmcallcenter.blob.core.windows.net/";
  if (hostname.includes("localhost") || hostname.includes(".dev.")) {
    url += "dev/";
  } else if (hostname.includes(".qa.")) {
    url += "quality/";
  } else if (hostname.includes(".uat.")) {
    url += "uat/";
  } else {
    url += "prod/";
  }
  url += storageId;
  return url;
}
